import { useMemo } from 'react';
import { useGetDefensives, useGetFertilizers } from '@practium/features/api';
import type { FertilizationPlanAdvice } from '../types';
import { toMixtureProductKey } from './useMixtureComposer';

export const useMixtureProductCatalog = (advice: FertilizationPlanAdvice) => {
	const { data: fertilizers = [] } = useGetFertilizers({ limit: 300 });
	const { data: defensives = [] } = useGetDefensives({ limit: 300 });
	const comboProducts = advice.generatedCombo?.products;
	const comboSelection = advice.generatedCombo?.selection;

	const catalog = useMemo(
		() => [
			...(comboProducts ?? []).map((product) => ({
				_id: String(product._id),
				name: product.name ?? '',
			})),
			...fertilizers.map((product) => ({ _id: product._id, name: product.name })),
			...defensives.map((product) => ({ _id: product._id, name: product.commercialName ?? '' })),
		],
		[comboProducts, defensives, fertilizers]
	);

	const comboProductIds = useMemo(
		() =>
			new Set(
				[
					...(comboProducts ?? []).map((product) => String(product._id)),
					...Object.values(comboSelection ?? {}),
				].filter((id): id is string => !!id)
			),
		[comboProducts, comboSelection]
	);

	const productOptions = useMemo(
		() => [
			...fertilizers
				.filter((product) => !comboProductIds.has(product._id))
				.map((product) => ({
					value: toMixtureProductKey('fertilizer', product._id),
					label: product.name,
				})),
			...defensives.map((product) => ({
				value: toMixtureProductKey('defensive', product._id),
				label: product.commercialName ?? '',
			})),
		],
		[comboProductIds, defensives, fertilizers]
	);

	const comboProductOptions = useMemo(() => {
		const names = new Map(catalog.map((product) => [product._id, product.name]));
		return [...comboProductIds].map((id) => ({
			value: toMixtureProductKey('fertilizer', id),
			label: names.get(id) || 'Produto do combo',
		}));
	}, [catalog, comboProductIds]);

	return { catalog, productOptions, comboProductOptions };
};
